import React, { useCallback } from 'react';

import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ConfirmationDialog } from '@eika/confirmation-dialog';

import type { PaymentAgreementId, ApprovalStatus, RecurringPaymentAgreement } from '../../../generated-types';
import { useShowTransactionResult } from '../../../hooks/useShowPaymentResult';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { deleteRecurringPaymentAgreement } from '../../../services/recurringPaymentsService';
import SigningContextFlow from '../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../signingContext/signingContext';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';
import StopFuturePaymentTransactionResult from '../../FuturePayment/StopFuturePaymentTransactionResult';

interface Props {
    recurringPayment: RecurringPaymentAgreement;
    paymentOperationPending: boolean;
    paymentStatus: ApprovalStatus | undefined;
    showDialogue: boolean;
    closeDialogue: () => void;
}

const DeleteRecurringPayment: React.FC<Props> = ({
    recurringPayment,
    closeDialogue,
    paymentOperationPending,
    paymentStatus,
    showDialogue,
}) => {
    const translated = useTranslation();

    const sign = (paymentAgreementId: PaymentAgreementId) => deleteRecurringPaymentAgreement(paymentAgreementId);

    const { signingError, handleCancelSigningFlow, startSigning } = useSigningContext(sign);

    const handleDeleteRecurringPayment = useCallback(() => {
        startSigning(recurringPayment.id);
    }, [startSigning, recurringPayment.id]);

    const showTransactionResult = useShowTransactionResult();

    if (showTransactionResult) {
        return (
            <StopFuturePaymentTransactionResult
                paymentType={recurringPayment.paymentType === 'PAYMENT' ? 'RECURRING_PAYMENT' : 'RECURRING_TRANSFER'}
                paymentStatus={paymentStatus}
                paymentError={signingError}
                retry={signingError ? handleCancelSigningFlow : closeDialogue}
                onClose={closeDialogue}
            />
        );
    }

    return (
        <SigningContextFlow onClosePayment={closeDialogue}>
            <ConfirmationDialog
                title={format(
                    translated.recurringPayments.sureYouWantToDelete,
                    translated.recurringPaymentType[recurringPayment.paymentType],
                )}
                id={`delete-recurring-pyment-dialog-${recurringPayment.id}`}
                className="delete-recurring-payment__dialogue"
                footer={
                    <div className="delete-recurring-payment-button__button-row">
                        <LinkButton
                            onClick={() => closeDialogue()}
                            disabled={paymentOperationPending}
                            data-track-id={paymentTrackIDs.recurring.delete}
                            data-track-linkname={translated.cancel}
                        >
                            {translated.cancel}
                        </LinkButton>
                        <ButtonWithSpinner
                            size="small"
                            buttonType="positive"
                            onClick={handleDeleteRecurringPayment}
                            showSpinner={paymentOperationPending}
                            data-track-id={paymentTrackIDs.recurring.delete}
                            data-track-linkname="submit"
                        >
                            {translated.confirmDelete}
                        </ButtonWithSpinner>
                    </div>
                }
                show={showDialogue}
                onExitFocus={() => closeDialogue()}
                headingLevel={3}
            />
        </SigningContextFlow>
    );
};

export default DeleteRecurringPayment;
